import { assertNotEmpty } from '@festina-life/core'
import { Card, CardBody, Icon } from '@festina-life/ui/structure'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useComparableField } from '../../context/useComparableField'
import type { TemplateVersion } from '../../types/templateModel'
import { BlockIndicator } from '../blockIndicator/BlockIndicator'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { LayerIndicator } from '../layerIndicator/LayerIndicator'
import { TemplateVersionContent } from '../templateDetails/content/TemplateVersionContent'
import { TemplateDocumentationTooltip } from '../templateDocumentationTooltip/TemplateDocumentationTooltip'

export const TemplateVersionNameCell = () => {
  const { templateVersion } = useTemplateContext()
  assertNotEmpty(templateVersion)

  const comparableField = useComparableField<TemplateVersion>(
    templateVersion.path,
  )

  const cellContents = templateVersion.versionInfo.identifier

  const serialiseComparableTemplateVersion = useCallback(
    ({ value: version }: SerialiseValueParams<TemplateVersion>) => {
      if (!version) {
        return ''
      }

      const {
        replaces,
        documentation,
        versionInfo: { parentId },
      } = version

      return stringifyComparableValue({
        documentation,
        parentId,
        replaces,
      })
    },
    [],
  )

  return (
    <div className="relative flex size-full flex-row">
      <TemplateDocumentationTooltip
        tip={
          <Card>
            <CardBody
              outerClassName="min-w-250 max-w-(--breakpoint-2xl)"
              innerClassName="grid grid-cols-[auto_1fr] items-start gap-x-10 gap-y-6"
            >
              <TemplateVersionContent templateVersion={templateVersion} />
            </CardBody>
          </Card>
        }
        className="h-full grow"
      >
        <div className="px-2 h-full flex flex-col gap-y-1 justify-center">
          <h4 className="m-0 font-medium">{cellContents}</h4>
          <div className="flex flex-row items-center gap-x-2 text-sm opacity-70">
            <BlockIndicator
              block={templateVersion.block}
              shouldShowIcon={false}
              isSecondaryBackground={false}
            />
            <LayerIndicator
              layer={templateVersion.layer}
              isSecondaryBackground={false}
            />
            <Icon name="Folder" />
            {!!templateVersion.versionInfo.package &&
              templateVersion.versionInfo.package.join('.')}
          </div>
        </div>
      </TemplateDocumentationTooltip>
      <div className="mx-2 w-10">
        <ValueSetupDiffIndicators
          shouldHideInheritedDiff
          comparableValues={comparableField}
          serialiseValueFn={serialiseComparableTemplateVersion}
        />
      </div>
    </div>
  )
}
